import React, { useEffect, useState } from "react";
import { Button, Card, Checkbox, Input, Select, TimePicker } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  useGetAlertEmailQuery,
  useGetAlertSMSQuery,
  useUpdateAlertEmailMutation,
  useUpdateAlertSMSMutation,
  useUpdateTimezoneMutation,
  useGetTimezoneQuery,
} from "./settings.service";
import { setTemperatureType } from "../temperatures/temperature.slice";
import dayjs from "dayjs";
import "./style.css";
import {
  useGetRestaurantChecklistDuetimeQuery,
  useGetRestaurantIncidentDuetimeQuery,
  useSetRestaurantChecklistDuetimeMutation,
  useSetRestaurantIncidentDuetimeMutation,
} from "../restaurant/restaruant.service";
import { useTimezone } from "../../provider/timezoneprovider";
import { getDatesOfThisWeek } from "../../utils/getDatesOfThisWeek";

// Timezone list with proper identifiers and display names
const timezoneList = [
  { value: "Europe/London", label: "GMT+1:00 London, Edinburgh" },
  { value: "Europe/Paris", label: "GMT+2:00 Paris, Berlin" },
  { value: "Europe/Moscow", label: "GMT+3:00 Moscow, Istanbul" },
  { value: "Asia/Dubai", label: "GMT+4:00 Dubai, Abu Dhabi" },
  { value: "Asia/Karachi", label: "GMT+5:00 Karachi, Islamabad" },
  { value: "Asia/Dhaka", label: "GMT+6:00 Dhaka, Colombo" },
  { value: "Asia/Bangkok", label: "GMT+7:00 Bangkok, Jakarta" },
  { value: "Asia/Shanghai", label: "GMT+8:00 Beijing, Shanghai" },
  { value: "Asia/Tokyo", label: "GMT+9:00 Tokyo, Seoul" },
  { value: "Australia/Sydney", label: "GMT+10:00 Sydney, Melbourne" },
  { value: "Pacific/Auckland", label: "GMT+12:00 Auckland, Wellington" },
  { value: "America/New_York", label: "GMT-5:00 New York, Washington" },
  { value: "America/Chicago", label: "GMT-6:00 Chicago, Dallas" },
  { value: "America/Denver", label: "GMT-7:00 Denver, Phoenix" },
  {
    value: "America/Los_Angeles",
    label: "GMT-8:00 Los Angeles, San Francisco",
  },
  { value: "America/Anchorage", label: "GMT-9:00 Anchorage, Juneau" },
  { value: "Pacific/Honolulu", label: "GMT-10:00 Honolulu" },
];

export default function Settings() {
  const { t, i18n } = useTranslation();
  const { setTimezone: setGlobalTimezone } = useTimezone();
  const dispatch = useDispatch();
  const tempType = useSelector((store) => store.temperature.temperatureType);
  const [hotHoldChillCheck, setHotHoldChillCheck] = useState([]);
  const [weeklyCheck, setWeeklyCheck] = useState([]);
  const curLanguage = i18n.language;

  const [alertEmail, setAlertEmail] = useState();
  const [alertSMS, setAlertSMS] = useState("");
  const [openingCheck, setOpeningCheck] = useState([]);
  const [closingCheck, setClosingCheck] = useState([]);
  const [incident, setIncident] = useState([]);
  const [timezone, setTimezone] = useState("");

  const { data = {}, isSuccess: isGetAlertEmailSuccess } =
    useGetAlertEmailQuery();
  const { data: alertSMSData = {}, isSuccess: isGetSMSSuccess } =
    useGetAlertSMSQuery();
  const { data: timezoneData = {}, isSuccess: isGetTimezoneSuccess } =
    useGetTimezoneQuery();

  const [requestUpdateAlertEmail, { isSuccess }] =
    useUpdateAlertEmailMutation();
  const [requestUpdateAlertSMS, { isSuccess: isUpdateSMSSuccess }] =
    useUpdateAlertSMSMutation();
  const [requestUpdateTimezone] = useUpdateTimezoneMutation();

  const {
    data: checkDueTimeData = {
      openingDueTime: [],
      closingDueTime: [],
      hotHoldDueTime: [],
      weeklyDueTime: [],
    },
    isSuccess: isGetOpeningCheckSuccess,
  } = useGetRestaurantChecklistDuetimeQuery();
  const [requestSetRestaurantChecklistDuetime] =
    useSetRestaurantChecklistDuetimeMutation();
  const {
    data: incidentDueTimeData = { incidentDueTime: [] },
    isSuccess: isGetIncidentDueTimeSuccess,
  } = useGetRestaurantIncidentDuetimeQuery();
  const [requestSetRestaurantIncidentDuetime] =
    useSetRestaurantIncidentDuetimeMutation();
  const { data: hotHoldData = { dueTime: [] }, isSuccess: isHotHoldSuccess } =
    useGetRestaurantChecklistDuetimeQuery("hotHoldChill");
  const { data: weeklyData = { dueTime: [] }, isSuccess: isWeeklySuccess } =
    useGetRestaurantChecklistDuetimeQuery("weekly");

  const [requestSetHotHoldCheck] = useSetRestaurantChecklistDuetimeMutation();
  const [requestSetWeeklyCheck] = useSetRestaurantChecklistDuetimeMutation();
  const handleChangeLanguage = (language) => {
    i18n.changeLanguage(language);
    localStorage.setItem("lng", language);
  };

  useEffect(() => {
    if (isGetIncidentDueTimeSuccess)
      setIncident(
        incidentDueTimeData.incidentDueTime.reduce((arr, cur) => {
          arr.push(cur.split(".")[0].replace("T", " "));
          return arr;
        }, [])
      );
  }, [incidentDueTimeData]);

  useEffect(() => {
    if (isGetSMSSuccess)
      setAlertSMS(
        alertSMSData.data?.sms ? `${alertSMSData.data?.sms.slice(1)}` : ""
      );
  }, [isGetSMSSuccess]);
  useEffect(() => {
    if (isHotHoldSuccess && hotHoldData?.dueTime) {
      const defaultTime = dayjs().hour(10).minute(0);
      const times =
        hotHoldData.dueTime.length > 0
          ? hotHoldData.dueTime.map((cur) =>
              cur.split(".")[0].replace("T", " ")
            )
          : [defaultTime.format("YYYY-MM-DD HH:mm:ss")];
      setHotHoldChillCheck(times);
    }
  }, [isHotHoldSuccess, hotHoldData]);

  useEffect(() => {
    if (isWeeklySuccess && weeklyData?.dueTime) {
      const defaultTime = dayjs().hour(10).minute(0);
      const times =
        weeklyData.dueTime.length > 0
          ? weeklyData.dueTime.map((cur) => cur.split(".")[0].replace("T", " "))
          : [defaultTime.format("YYYY-MM-DD HH:mm:ss")];
      setWeeklyCheck(times);
    }
  }, [isWeeklySuccess, weeklyData]);

  useEffect(() => {
    if (isGetOpeningCheckSuccess) {
      setOpeningCheck(
        checkDueTimeData.openingDueTime.reduce((arr, cur) => {
          arr.push(cur.split(".")[0].replace("T", " "));
          return arr;
        }, [])
      );
      setClosingCheck(
        checkDueTimeData.closingDueTime.reduce((arr, cur) => {
          arr.push(cur.split(".")[0].replace("T", " "));
          return arr;
        }, [])
      );
      setHotHoldChillCheck(
        checkDueTimeData.hotHoldDueTime.reduce((arr, cur) => {
          arr.push(cur.split(".")[0].replace("T", " "));
          return arr;
        }, [])
      );

      setWeeklyCheck(
        checkDueTimeData.weeklyDueTime.reduce((arr, cur) => {
          arr.push(cur.split(".")[0].replace("T", " "));
          return arr;
        }, [])
      );
    }
  }, [checkDueTimeData]);

  useEffect(() => {
    if (isGetAlertEmailSuccess) setAlertEmail(data.data?.alertEmail);
  }, [data]);

  useEffect(() => {
    if (isGetTimezoneSuccess && timezoneData?.timezone) {
      setTimezone(timezoneData.timezone);
    }
  }, [timezoneData]);

  const getDayOfWeek = (targetDay) => {
    const today = dayjs();
    const currentDay = today.day();

    let daysUntilTarget = targetDay - currentDay;
    if (daysUntilTarget > 0) {
      daysUntilTarget -= 7;
    }

    const targetDate = today.add(daysUntilTarget, "day");

    return targetDate;
  };

  const handleCheckDay = (day, type) => {
    const getTargetDay = getDayOfWeek(day);
    if (type === "closing") {
      requestSetRestaurantChecklistDuetime({
        type,
        data: {
          closingDueTime: closingCheck
            .reduce((arr, cur) => {
              arr.push(dayjs(cur).get("day"));
              return arr;
            }, [])
            .includes(day)
            ? closingCheck.reduce((arr, cur) => {
                if (dayjs(cur).get("day") !== day) arr.push(cur);
                return arr;
              }, [])
            : [
                ...closingCheck,
                dayjs(getTargetDay)
                  .set("hour", dayjs(closingCheck[0]).get("hours"))
                  .set("minute", dayjs(closingCheck[0]).get("minutes"))
                  .format("YYYY-MM-DD HH:mm:ss"),
              ],
        },
      });
    }
    if (type === "opening") {
      const openingDays = openingCheck.reduce((arr, cur) => {
        arr.push(dayjs(cur).get("day"));
        return arr;
      }, []);
      const openingTimes =
        openingCheck.length > 0
          ? openingCheck[0].split(" ")[1].slice(0, 5)
          : "00:00";

      let openingDueTime = [];
      if (openingDays.includes(day)) {
        openingDueTime = getDatesOfThisWeek(
          openingDays.filter((item) => item !== day),
          openingTimes
        );
      } else {
        openingDueTime = getDatesOfThisWeek(
          [...openingDays, day],
          openingTimes
        );
      }
      requestSetRestaurantChecklistDuetime({
        type,
        data: {
          openingDueTime,
        },
      });
    }
  };

  const handleIncidentDay = (day) => {
    requestSetRestaurantIncidentDuetime({
      incidentDueTime: incident
        .reduce((arr, cur) => {
          arr.push(dayjs(cur).get("day"));
          return arr;
        }, [])
        .includes(day)
        ? incident.reduce((arr, cur) => {
            if (dayjs(cur).get("day") !== day) arr.push(cur);
            return arr;
          }, [])
        : [
            ...incident,
            dayjs(incident[0])
              .set("date", dayjs().day(day).format("DD"))
              .format("YYYY-MM-DD HH:mm:ss"),
          ],
    });
  };

  const handleTimezoneChange = (value) => {
    setTimezone(value);
    setGlobalTimezone(value);
    requestUpdateTimezone({ timezone: value });
  };
  const handleHotHoldDay = (day) => {
    const targetDate = getDayOfWeek(day);
    const updated = hotHoldChillCheck.reduce((arr, cur) => {
      arr.push(dayjs(cur).get("day"));
      return arr;
    }, []);

    const newTimes = updated.includes(day)
      ? hotHoldChillCheck.filter((cur) => dayjs(cur).get("day") !== day)
      : [
          ...hotHoldChillCheck,
          dayjs(targetDate)
            .set("hour", dayjs(hotHoldChillCheck[0] || dayjs()).get("hour"))
            .set("minute", dayjs(hotHoldChillCheck[0] || dayjs()).get("minute"))
            .format("YYYY-MM-DD HH:mm:ss"),
        ];

    requestSetHotHoldCheck({
      type: "hotHoldChill",
      data: { dueTime: newTimes },
    });
  };

  const handleWeeklyDay = (day) => {
    const targetDate = getDayOfWeek(day);
    const updated = weeklyCheck.reduce((arr, cur) => {
      arr.push(dayjs(cur).get("day"));
      return arr;
    }, []);

    const newTimes = updated.includes(day)
      ? weeklyCheck.filter((cur) => dayjs(cur).get("day") !== day)
      : [
          ...weeklyCheck,
          dayjs(targetDate)
            .set("hour", dayjs(weeklyCheck[0] || dayjs()).get("hour"))
            .set("minute", dayjs(weeklyCheck[0] || dayjs()).get("minute"))
            .format("YYYY-MM-DD HH:mm:ss"),
        ];

    requestSetWeeklyCheck({ type: "weekly", data: { dueTime: newTimes } });
  };

  return (
    <Card className="setting-page" bordered={false}>
      <div className="setting-page-module">
        <div className="setting-page-module-label">{t("LANGUAGE")}</div>
        <div className="setting-page-module-select">
          <Select
            style={{ width: "120px" }}
            value={curLanguage}
            options={[
              { label: t("ENGLISH"), value: "en" },
              { label: t("CHINESE"), value: "ch" },
              { label: t("ITALIAN"), value: "it" },
              { label: t("TURKISH"), value: "tu" },
              { label: t("POLISH"), value: "po" },
              { label: t("JAPANESE"), value: "jp" },
              { label: t("HINDI"), value: "hi" },
              { label: t("ARABIC"), value: "ar" },
              { label: t("ROMANIAN"), value: "ro" },
            ]}
            onChange={handleChangeLanguage}
          />
        </div>
      </div>
      <div className="setting-page-module">
        <div className="setting-page-module-label">{t("TIMEZONE")}</div>
        <div className="setting-page-module-select">
          <Select
            style={{ width: "300px" }}
            value={timezone}
            options={timezoneList}
            onChange={handleTimezoneChange}
            showSearch
            filterOption={(input, option) =>
              option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          />
        </div>
      </div>
      <div className="setting-page-module">
        <div className="setting-page-module-label">{t("METRIC")}</div>
        <div className="setting-page-module-select">
          <Select
            style={{ width: "120px" }}
            value={tempType}
            options={[
              { label: "CELSIUS", value: "celsius" },
              { label: "FAHRENHEIT", value: "fahrenheit" },
            ]}
            onChange={(e) => dispatch(setTemperatureType(e))}
          />
        </div>
      </div>
      <div className="setting-page-module">
        <div className="setting-page-module-label">{t("ALERTS")}</div>
        <div className="setting-page-module-select setting-page-module-alert">
          <div className="setting-page-module-select-item">
            <div className="setting-page-module-select-item-label">EMAIL</div>
            <Input
              onChange={(e) => setAlertEmail(e.target.value)}
              value={alertEmail}
            />
            <Button
              style={{ marginLeft: "20px" }}
              type="primary"
              onClick={() =>
                alertEmail &&
                alertEmail !== "" &&
                requestUpdateAlertEmail({ alertEmail })
              }
            >
              {t("UPDATE")}
            </Button>
          </div>
          <div
            className="setting-page-module-select-item"
            style={{ marginTop: "10px" }}
          >
            <div className="setting-page-module-select-item-label">SMS</div>
            <Input
              value={alertSMS}
              onChange={(e) => setAlertSMS(e.target.value)}
              addonBefore="+"
              style={{ maxWidth: "250px" }}
            />
            <Button
              style={{ marginLeft: "20px" }}
              type="primary"
              onClick={() =>
                alertSMS &&
                alertSMS !== "" &&
                requestUpdateAlertSMS({ sms: `+${alertSMS}` })
              }
            >
              {t("UPDATE")}
            </Button>
          </div>
        </div>
      </div>
      <div className="setting-page-module">
        <div className="setting-page-module-label">
          {t("OPENING CHECKS")}
          <br />
          {t("TIMES")}
          <br />
          {t("DAY")}
        </div>
        <div className="setting-page-module-select setting-page-module-alert">
          <div>
            {t("COMPLETED BY")}
            <TimePicker
              allowClear={false}
              placeholder=""
              style={{ marginLeft: "10px" }}
              format={"HH:mm"}
              value={openingCheck[0] && dayjs(openingCheck[0])}
              onChange={(time, timeString) => {
                requestSetRestaurantChecklistDuetime({
                  type: "opening",
                  data: {
                    openingDueTime:
                      openingCheck.length > 0
                        ? getDatesOfThisWeek(
                            openingCheck.reduce((arr, cur) => {
                              arr.push(dayjs(cur).get("day"));
                              return arr;
                            }, []),
                            timeString
                          )
                        : getDatesOfThisWeek([new Date().getDay()], timeString),
                  },
                });
              }}
            />
          </div>
          <div className="setting-page-module-select">
            <div
              className="setting-page-module-select-item"
              onClick={() => handleCheckDay(1, "opening")}
            >
              <Checkbox
                checked={
                  openingCheck
                    .reduce((arr, cur) => {
                      arr.push(dayjs(cur).get("day"));
                      return arr;
                    }, [])
                    .includes(1)
                    ? true
                    : false
                }
              />
              <div
                style={{ marginLeft: "10px" }}
                className={
                  openingCheck
                    .reduce((arr, cur) => {
                      arr.push(dayjs(cur).get("day"));
                      return arr;
                    }, [])
                    .includes(1)
                    ? ""
                    : "shade-effect"
                }
              >
                {t("MON")}
              </div>
            </div>
            <div
              className={`setting-page-module-select-item`}
              onClick={() => handleCheckDay(2, "opening")}
            >
              <Checkbox
                checked={
                  openingCheck
                    .reduce((arr, cur) => {
                      arr.push(dayjs(cur).get("day"));
                      return arr;
                    }, [])
                    .includes(2)
                    ? true
                    : false
                }
              />
              <div
                style={{ marginLeft: "10px" }}
                className={
                  openingCheck
                    .reduce((arr, cur) => {
                      arr.push(dayjs(cur).get("day"));
                      return arr;
                    }, [])
                    .includes(2)
                    ? ""
                    : "shade-effect"
                }
              >
                {t("TUES")}
              </div>
            </div>
            <div
              className={`setting-page-module-select-item`}
              onClick={() => handleCheckDay(3, "opening")}
            >
              <Checkbox
                checked={
                  openingCheck
                    .reduce((arr, cur) => {
                      arr.push(dayjs(cur).get("day"));
                      return arr;
                    }, [])
                    .includes(3)
                    ? true
                    : false
                }
              />
              <div
                style={{ marginLeft: "10px" }}
                className={
                  openingCheck
                    .reduce((arr, cur) => {
                      arr.push(dayjs(cur).get("day"));
                      return arr;
                    }, [])
                    .includes(3)
                    ? ""
                    : "shade-effect"
                }
              >
                {t("WED")}
              </div>
            </div>
            <div
              className={`setting-page-module-select-item`}
              onClick={() => handleCheckDay(4, "opening")}
            >
              <Checkbox
                checked={
                  openingCheck
                    .reduce((arr, cur) => {
                      arr.push(dayjs(cur).get("day"));
                      return arr;
                    }, [])
                    .includes(4)
                    ? true
                    : false
                }
              />
              <div
                style={{ marginLeft: "10px" }}
                className={
                  openingCheck
                    .reduce((arr, cur) => {
                      arr.push(dayjs(cur).get("day"));
                      return arr;
                    }, [])
                    .includes(4)
                    ? ""
                    : "shade-effect"
                }
              >
                {t("THU")}
              </div>
            </div>
            <div
              className={`setting-page-module-select-item`}
              onClick={() => handleCheckDay(5, "opening")}
            >
              <Checkbox
                checked={
                  openingCheck
                    .reduce((arr, cur) => {
                      arr.push(dayjs(cur).get("day"));
                      return arr;
                    }, [])
                    .includes(5)
                    ? true
                    : false
                }
              />
              <div
                style={{ marginLeft: "10px" }}
                className={
                  openingCheck
                    .reduce((arr, cur) => {
                      arr.push(dayjs(cur).get("day"));
                      return arr;
                    }, [])
                    .includes(5)
                    ? ""
                    : "shade-effect"
                }
              >
                {t("FRI")}
              </div>
            </div>
            <div
              className={`setting-page-module-select-item`}
              onClick={() => handleCheckDay(6, "opening")}
            >
              <Checkbox
                checked={
                  openingCheck
                    .reduce((arr, cur) => {
                      arr.push(dayjs(cur).get("day"));
                      return arr;
                    }, [])
                    .includes(6)
                    ? true
                    : false
                }
              />
              <div
                style={{ marginLeft: "10px" }}
                className={
                  openingCheck
                    .reduce((arr, cur) => {
                      arr.push(dayjs(cur).get("day"));
                      return arr;
                    }, [])
                    .includes(6)
                    ? ""
                    : "shade-effect"
                }
              >
                {t("SAT")}
              </div>
            </div>
            <div
              className={`setting-page-module-select-item`}
              onClick={() => handleCheckDay(0, "opening")}
            >
              <Checkbox
                checked={
                  openingCheck
                    .reduce((arr, cur) => {
                      arr.push(dayjs(cur).get("day"));
                      return arr;
                    }, [])
                    .includes(0)
                    ? true
                    : false
                }
              />
              <div
                style={{ marginLeft: "10px" }}
                className={
                  openingCheck
                    .reduce((arr, cur) => {
                      arr.push(dayjs(cur).get("day"));
                      return arr;
                    }, [])
                    .includes(0)
                    ? ""
                    : "shade-effect"
                }
              >
                {t("SUN")}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="setting-page-module">
        <div className="setting-page-module-label">
          {t("PROBE CHECKS")}
          <br />
          {t("TIMES")}
          <br />
          {t("DAY")}
        </div>
        <div className="setting-page-module-select setting-page-module-alert">
          <div>
            {t("COMPLETED BY")}
            <TimePicker
              allowClear={false}
              placeholder=""
              style={{ marginLeft: "10px" }}
              format={"HH:mm"}
              value={
                hotHoldChillCheck.length > 0
                  ? dayjs(hotHoldChillCheck[0])
                  : null
              }
              onChange={(time, timeString) => {
                requestSetHotHoldCheck({
                  type: "hotHoldChill",
                  data: {
                    dueTime:
                      hotHoldChillCheck.length > 0
                        ? getDatesOfThisWeek(
                            hotHoldChillCheck.reduce((arr, cur) => {
                              arr.push(dayjs(cur).get("day"));
                              return arr;
                            }, []),
                            timeString
                          )
                        : getDatesOfThisWeek([new Date().getDay()], timeString),
                  },
                });
              }}
            />
          </div>
          <div className="setting-page-module-select">
            {[0, 1, 2, 3, 4, 5, 6].map((day) => (
              <div
                key={`hotHold-${day}`}
                className="setting-page-module-select-item"
                onClick={() => handleHotHoldDay(day)}
              >
                <Checkbox
                  checked={hotHoldChillCheck.some(
                    (cur) => dayjs(cur).day() === day
                  )}
                />
                <div
                  style={{ marginLeft: "10px" }}
                  className={
                    hotHoldChillCheck.some((cur) => dayjs(cur).day() === day)
                      ? ""
                      : "shade-effect"
                  }
                >
                  {t(["SUN", "MON", "TUES", "WED", "THU", "FRI", "SAT"][day])}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="setting-page-module">
        <div className="setting-page-module-label">
          {t("CLOSING CHECKS")}
          <br />
          {t("TIMES")}
          <br />
          {t("DAY")}
        </div>
        <div className="setting-page-module-select setting-page-module-alert">
          <div>
            {t("COMPLETED BY")}
            <TimePicker
              allowClear={false}
              placeholder=""
              style={{ marginLeft: "10px" }}
              format={"HH:mm"}
              value={closingCheck[0] && dayjs(closingCheck[0])}
              onChange={(time, timeString) => {
                requestSetRestaurantChecklistDuetime({
                  type: "closing",
                  data: {
                    closingDueTime:
                      closingCheck.length > 0
                        ? getDatesOfThisWeek(
                            closingCheck.reduce((arr, cur) => {
                              arr.push(dayjs(cur).get("day"));
                              return arr;
                            }, []),
                            timeString
                          )
                        : getDatesOfThisWeek([new Date().getDay()], timeString),
                  },
                });
              }}
            />
          </div>
          <div className="setting-page-module-select">
            <div
              className={`setting-page-module-select-item`}
              onClick={() => handleCheckDay(1, "closing")}
            >
              <Checkbox
                checked={
                  closingCheck
                    .reduce((arr, cur) => {
                      arr.push(dayjs(cur).get("day"));
                      return arr;
                    }, [])
                    .includes(1)
                    ? true
                    : false
                }
              />
              <div
                style={{ marginLeft: "10px" }}
                className={
                  closingCheck
                    .reduce((arr, cur) => {
                      arr.push(dayjs(cur).get("day"));
                      return arr;
                    }, [])
                    .includes(1)
                    ? ""
                    : "shade-effect"
                }
              >
                {t("MON")}
              </div>
            </div>
            <div
              className={`setting-page-module-select-item`}
              onClick={() => handleCheckDay(2, "closing")}
            >
              <Checkbox
                checked={
                  closingCheck
                    .reduce((arr, cur) => {
                      arr.push(dayjs(cur).get("day"));
                      return arr;
                    }, [])
                    .includes(2)
                    ? true
                    : false
                }
              />
              <div
                style={{ marginLeft: "10px" }}
                className={
                  closingCheck
                    .reduce((arr, cur) => {
                      arr.push(dayjs(cur).get("day"));
                      return arr;
                    }, [])
                    .includes(2)
                    ? ""
                    : "shade-effect"
                }
              >
                {t("TUES")}
              </div>
            </div>
            <div
              className={`setting-page-module-select-item`}
              onClick={() => handleCheckDay(3, "closing")}
            >
              <Checkbox
                checked={
                  closingCheck
                    .reduce((arr, cur) => {
                      arr.push(dayjs(cur).get("day"));
                      return arr;
                    }, [])
                    .includes(3)
                    ? true
                    : false
                }
              />
              <div
                style={{ marginLeft: "10px" }}
                className={
                  closingCheck
                    .reduce((arr, cur) => {
                      arr.push(dayjs(cur).get("day"));
                      return arr;
                    }, [])
                    .includes(3)
                    ? ""
                    : "shade-effect"
                }
              >
                {t("WED")}
              </div>
            </div>
            <div
              className={`setting-page-module-select-item`}
              onClick={() => handleCheckDay(4, "closing")}
            >
              <Checkbox
                checked={
                  closingCheck
                    .reduce((arr, cur) => {
                      arr.push(dayjs(cur).get("day"));
                      return arr;
                    }, [])
                    .includes(4)
                    ? true
                    : false
                }
              />
              <div
                style={{ marginLeft: "10px" }}
                className={
                  closingCheck
                    .reduce((arr, cur) => {
                      arr.push(dayjs(cur).get("day"));
                      return arr;
                    }, [])
                    .includes(4)
                    ? ""
                    : "shade-effect"
                }
              >
                {t("THU")}
              </div>
            </div>
            <div
              className={`setting-page-module-select-item`}
              onClick={() => handleCheckDay(5, "closing")}
            >
              <Checkbox
                checked={
                  closingCheck
                    .reduce((arr, cur) => {
                      arr.push(dayjs(cur).get("day"));
                      return arr;
                    }, [])
                    .includes(5)
                    ? true
                    : false
                }
              />
              <div
                style={{ marginLeft: "10px" }}
                className={
                  closingCheck
                    .reduce((arr, cur) => {
                      arr.push(dayjs(cur).get("day"));
                      return arr;
                    }, [])
                    .includes(5)
                    ? ""
                    : "shade-effect"
                }
              >
                {t("FRI")}
              </div>
            </div>
            <div
              className={`setting-page-module-select-item`}
              onClick={() => handleCheckDay(6, "closing")}
            >
              <Checkbox
                checked={
                  closingCheck
                    .reduce((arr, cur) => {
                      arr.push(dayjs(cur).get("day"));
                      return arr;
                    }, [])
                    .includes(6)
                    ? true
                    : false
                }
              />
              <div
                style={{ marginLeft: "10px" }}
                className={
                  closingCheck
                    .reduce((arr, cur) => {
                      arr.push(dayjs(cur).get("day"));
                      return arr;
                    }, [])
                    .includes(6)
                    ? ""
                    : "shade-effect"
                }
              >
                {t("SAT")}
              </div>
            </div>
            <div
              className={`setting-page-module-select-item`}
              onClick={() => handleCheckDay(0, "closing")}
            >
              <Checkbox
                checked={
                  closingCheck
                    .reduce((arr, cur) => {
                      arr.push(dayjs(cur).get("day"));
                      return arr;
                    }, [])
                    .includes(0)
                    ? true
                    : false
                }
              />
              <div
                style={{ marginLeft: "10px" }}
                className={
                  closingCheck
                    .reduce((arr, cur) => {
                      arr.push(dayjs(cur).get("day"));
                      return arr;
                    }, [])
                    .includes(0)
                    ? ""
                    : "shade-effect"
                }
              >
                {t("SUN")}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* WEEKLY CHECKS */}
      <div className="setting-page-module">
        <div className="setting-page-module-label">
          {t("WEEKLY CHECKS")}
          <br />
          {t("TIMES")}
          <br />
          {t("DAY")}
        </div>
        <div className="setting-page-module-select setting-page-module-alert">
          <div>
            {t("COMPLETED BY")}
            <TimePicker
              allowClear={false}
              placeholder=""
              style={{ marginLeft: "10px" }}
              format={"HH:mm"}
              value={weeklyCheck.length > 0 ? dayjs(weeklyCheck[0]) : null}
              onChange={(time, timeString) => {
                requestSetWeeklyCheck({
                  type: "weekly",
                  data: {
                    dueTime:
                      weeklyCheck.length > 0
                        ? getDatesOfThisWeek(
                            weeklyCheck.reduce((arr, cur) => {
                              arr.push(dayjs(cur).get("day"));
                              return arr;
                            }, []),
                            timeString
                          )
                        : getDatesOfThisWeek([new Date().getDay()], timeString),
                  },
                });
              }}
            />
          </div>
          <div className="setting-page-module-select">
            {[0, 1, 2, 3, 4, 5, 6].map((day) => (
              <div
                key={`weekly-${day}`}
                className="setting-page-module-select-item"
                onClick={() => handleWeeklyDay(day)}
              >
                <Checkbox
                  checked={weeklyCheck.some((cur) => dayjs(cur).day() === day)}
                />
                <div
                  style={{ marginLeft: "10px" }}
                  className={
                    weeklyCheck.some((cur) => dayjs(cur).day() === day)
                      ? ""
                      : "shade-effect"
                  }
                >
                  {t(["SUN", "MON", "TUES", "WED", "THU", "FRI", "SAT"][day])}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="setting-page-module" style={{ marginTop: 24 }}>
        <div style={{ fontWeight: 600, marginBottom: 8 }}>
          Order printer supplies (Amazon)
        </div>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          <Button
            type="primary"
            href="https://www.amazon.co.uk/SFBB-Food-Date-Label-Printer/dp/B0H81H7BZD/"
            target="_blank"
            rel="noopener noreferrer"
          >
            Order label printer
          </Button>
          <Button
            href="https://www.amazon.co.uk/Guardstar-Food-Label-Printer-Labels/dp/B0H7CVFPNK"
            target="_blank"
            rel="noopener noreferrer"
          >
            Order printer rolls
          </Button>
        </div>
      </div>

      <div className="setting-page-module" style={{ fontSize: "16px" }}>
        version 1.1
      </div>
    </Card>
  );
}
